import classnames from 'classnames';
import React, { useId } from 'react';

import '@eika/radio-button-style/src/ExpandableRadioButtons.scss';

import ErrorMessageLabel from './ErrorMessageLabel';
import RadioButton from './RadioButton';
import { RadioButtonLabel } from './RadioButtonLabel';
import { ExpandableRadioButtonsProps } from './Types';
import { toDataTrackIdFormat } from './utils/toDataTrackIdFormat';

const ExpandableRadioButtons: React.FC<ExpandableRadioButtonsProps> = ({
    label,
    subLabel,
    name,
    required,
    errorMessage,
    infoButton,
    externalLinkLabel,
    selectedOption,
    onChange,
    className,
    dataTrackId = label,
    options,
    ...rest
}) => {
    const errorId = useId();

    return (
        <fieldset className={classnames('sgw-radio-button-panels sgw-expandable-radio-buttons', className)} {...rest}>
            {label && (
                <RadioButtonLabel
                    label={label}
                    sublabel={subLabel}
                    infoButton={infoButton}
                    externalLinkLabel={externalLinkLabel}
                />
            )}
            {options.map(({ expandContent, description, dataTrackLinkname, ...option }) => (
                <div
                    key={option.id ?? option.value}
                    className={classnames('sgw-expandable-radio-button-option__wrapper', {
                        checked: option.value === selectedOption,
                    })}
                >
                    <label
                        htmlFor={option.id ?? option.value}
                        className={classnames('sgw-expandable-radio-button-option')}
                    >
                        <RadioButton
                            {...option}
                            className={classnames('sgw-expandable-radio-button-option__input', option.className)}
                            selectedOption={selectedOption}
                            error={!!errorMessage}
                            required={required}
                            aria-invalid={!!errorMessage}
                            describedBy={errorId}
                            onChange={onChange}
                            name={name}
                            id={option.id ?? option.value}
                            data-track-id={toDataTrackIdFormat(dataTrackId)}
                            data-track-linkname={
                                dataTrackId
                                    ? toDataTrackIdFormat(dataTrackLinkname || option.value || description?.toString())
                                    : undefined
                            }
                            value={option.value}
                            externalLabel
                        />
                        {description && (
                            <span className="sgw-radio-button-panel-option-description">{description}</span>
                        )}
                    </label>

                    {option.value === selectedOption && expandContent && (
                        <div className="sgw-expandable-radio-buttons__content-wrapper">{expandContent}</div>
                    )}
                </div>
            ))}
            {errorMessage && <ErrorMessageLabel errorMessage={errorMessage} errorId={errorId} />}
        </fieldset>
    );
};

export default ExpandableRadioButtons;
